import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { DARK_COMPOUND } from './dark-tokens';
import { PAPER_ACCENT_INK } from './paper-tokens';
import { assertFilled } from './dev-invariants';
import { TONE, type LandingTone } from './tone';

type ChipCommon = {
  icon?: ReactElement;
  label: string;
  className?: string;
  /** R5-P3 fix pack item 4 [ADV-14] — orthogonal to the variant/tone
   * discriminated union below (spread into all three arms via `ChipCommon`,
   * not a fourth variant). 'fill' stretches the chip to its parent's
   * reserved box (`flex h-full w-full items-center justify-center`) instead
   * of the default inline-flex shrink-to-fit; default (undefined) is
   * byte-identical to pre-existing behavior. Introduced for the hero
   * "Convertește" CTA, which previously forced this exact shape with a
   * `!`-prefixed twMerge override chain on `className`
   * (`check:marketing-chip-bang-override` now bans that pattern) — a future
   * Chip base-class edit or class-list reorder could silently revert an
   * override chain (r5-defect-map.md §1.3c "layout snap" risk); a typed,
   * variant-internal prop cannot drift the same way. */
  layout?: 'fill';
};

export type ChipProps =
  | (ChipCommon & { variant: 'system' | 'keycap'; tone?: 'neutral'; dot?: never })
  | (ChipCommon & {
      variant: 'status';
      tone?: 'neutral';
      dot?: boolean;
      /** P2.5 COLOR & CONTRAST AMENDMENT §A/§B — additive, optional, defaults
       * to 'neutral' (byte-identical to pre-amendment rendering). Drives
       * fill/text/border from the single `tone.ts` palette. The amber arm
       * below is UNTOUCHED by this addition — amber stays pole-only,
       * reached only via `tone:'accent'`+`accentContext`, never via
       * `statusTone`. */
      statusTone?: LandingTone;
    })
  | (ChipCommon & {
      variant: 'status';
      tone: 'accent';
      /** Amber is a pole-only accent (Q2 LOCKED) — hero + final-CTA bands,
       * PLUS the one named "component-scale decision amber" exception:
       * the Pricing "Popular" badge (checkpoint-1 RESOLVED ruling 5,
       * r5-checkpoint-1.md, 2026-07-15: "'Popular' badge converts to the
       * Chip status/accent recipe in P3 — kills the last bespoke
       * treatment"). `check-landing-band-hue.mjs`'s own allowlist already
       * treats this exact amber trio as sanctioned "buttons/chips/pricing
       * badges" decision-amber, DISTINCT from the hero/final-CTA atmosphere
       * poles — widening this union to a literal `'pricing'` value lets
       * `PricingSection.tsx` read the REAL recipe below instead of
       * replicating its hex trio via a `className` override (R5-P3
       * post-review fix round; was flagged as an open cross-lane item
       * because Chip.tsx wasn't in the pricing lane's file ownership at
       * build time — this closes it). */
      accentContext: 'hero' | 'final-cta' | 'pricing';
      dot?: boolean;
    });

function isAccentChip(p: ChipProps): p is Extract<ChipProps, { tone: 'accent' }> {
  return p.variant === 'status' && p.tone === 'accent';
}

const BASE = 'inline-flex items-center gap-1 whitespace-nowrap';

/**
 * Chip — three variants only (R4-P2 build brief §2 + §6.1 [AMENDED b5],
 * dossier §1.4). Anatomy is copied exactly:
 *  - system: tag label, `1px #FFFFFF33` border, 10px uppercase 700.
 *  - status: badge, 13px `[font-weight:510]`, neutral fill `white/[0.06]`
 *    or (pole-only) accent fill `#f59e0b`/10.
 *  - keycap: raycast-style OS keycap, gradient fill, 11px.
 * Amber (`tone='accent'`) is a `status`-only, `accentContext`-gated
 * combination — `keycap`/`system` chips, and a dotted `system` chip, no
 * longer type-check (Q2: amber lives at hero + final-CTA poles only).
 */
export function Chip(props: ChipProps) {
  const { variant, icon, label, className, layout } = props;
  assertFilled(label.trim().length >= 1, 'Chip: label must be non-blank.');

  const accent = isAccentChip(props) ? props : null;
  // 2026-07-18 slop-audit revision pass FIX 1 (CRITICAL, amber-on-paper
  // contrast, context-driven per brief): `accentContext` has TWO real
  // consumers with different surfaces — 'hero' (`HeroJourneySceneCanvas.
  // tsx`, a DARK exhibit interior — bare `#f59e0b` stays correct there,
  // 1.9:1-ish contrast is a non-issue against a dark backdrop) and
  // 'pricing' (`PricingSection.tsx`'s "Popular" badge, real PAPER page
  // chrome — bare `#f59e0b` measures ~1.8:1 against paper, failing WCAG AA
  // 4.5:1). 'final-cta' has no live consumer today (grep-verified) but is
  // treated the same as 'hero' (a conversion-pole context, historically
  // dark) rather than assumed paper. Only the TEXT color is context-driven
  // here — the `bg-[#f59e0b]/10` wash below is a low-alpha FILL, not
  // read as a glyph, so text-contrast math doesn't apply to it and it
  // stays identical on both surfaces (Constitution: smallest change).
  const accentOnPaper = accent?.accentContext === 'pricing';
  const fill = layout === 'fill';
  const showDot = variant === 'status' && Boolean(props.dot);
  // P2.5 §A/§B: resolves ONLY on the status-non-accent arm; the accent
  // (amber) arm and system/keycap variants ignore this entirely — amber
  // stays reachable only through `tone:'accent'`.
  const statusTone: LandingTone =
    variant === 'status' && !accent
      ? ((props as { statusTone?: LandingTone }).statusTone ?? 'neutral')
      : 'neutral';
  const toneRecipe = TONE[statusTone];
  // §6.4 c9 DOM contract: every visible text node's nearest element carries
  // data-type-role. `system` is the dossier's literal "label" chip variant
  // (uppercase/tracked = label, per §1.2 "typeface + case + tracking
  // separates label from content, never a color"). `status` (non-accent)
  // renders at `white/80` — a value the six-role ladder (primary/secondary/
  // tertiary/quaternary/label/timestamp) has no slot for; R4-P2 correctness
  // review Finding M2 resolved this as a documented 7th role, `content`
  // (0.80 alpha), rather than silently reusing `secondary` (0.60) or
  // inventing an undocumented value. `status` (accent, amber) and `keycap`
  // are intentionally left unmarked: amber is a pole-only semantic accent
  // outside the neutral opacity ladder entirely, and keycap's `white/70` is
  // a third, narrower orphaned value the review didn't name — extending the
  // ladder further here would be undocumented scope beyond what was
  // reviewed.
  const typeRole =
    variant === 'system' ? 'label' : variant === 'status' && !accent ? 'content' : undefined;

  return (
    <span
      data-primitive="Chip"
      data-accent-context={accent?.accentContext}
      className={cn(
        BASE,
        // R5-P3 fix pack item 4: 'fill' layout, orthogonal to variant/tone —
        // stretches the chip to its parent's reserved box instead of the
        // default inline-flex shrink-to-fit. Placed right after BASE (ahead
        // of every variant string, same as BASE itself) so `className`'s own
        // twMerge-conflict resolution is untouched by this addition.
        fill && 'flex h-full w-full items-center justify-center',
        variant === 'system' &&
          'rounded-[4px] border border-[#FFFFFF33] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.03em] leading-none text-white/60',
        // P2.5 §A/§D: fill/text/border now resolve from `toneRecipe`
        // (`TONE.neutral` when `statusTone` is absent/'neutral' — byte-
        // identical fill to before except the §D contrast-ladder bump,
        // white/[0.06] -> white/[0.08]; text/border unchanged for
        // neutral). A real semantic tone additionally gets a visible
        // 1px border (§A's table: neutral is the only borderless row).
        variant === 'status' &&
          !accent && [
            'rounded-[7px] px-2 py-1 text-[13px] leading-[1.3] [font-weight:510]',
            toneRecipe.chipFill,
            toneRecipe.chipText,
            statusTone !== 'neutral' && ['border', toneRecipe.chipBorder],
          ],
        // R5-P3 item 4: the 'fill' arm picks a bigger radius + heavier
        // weight directly (rounded-[10px]/font-semibold, the hero CTA's own
        // needs) INSTEAD of emitting the pill's rounded-[7px]/[font-weight:
        // 510] and fighting it with a `!`-prefixed override — there is no
        // cascade to win against because the conflicting classes are never
        // emitted together in the first place.
        variant === 'status' &&
          accent && [
            fill
              ? 'rounded-[10px] bg-[#f59e0b]/10 px-2 py-1 text-[13px] leading-[1.3] font-semibold'
              : 'rounded-[7px] bg-[#f59e0b]/10 px-2 py-1 text-[13px] leading-[1.3] [font-weight:510]',
            accentOnPaper ? PAPER_ACCENT_INK.text : 'text-[#f59e0b]',
          ],
        variant === 'keycap' && [
          'h-5 rounded-[4px] border border-[#FFFFFF1A] px-1.5 text-[11px] font-medium text-white/70',
          DARK_COMPOUND.keycapGradientBg,
        ],
        className
      )}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="inline-flex size-3.5 shrink-0 items-center justify-center [&>svg]:size-full"
        >
          {icon}
        </span>
      )}
      {showDot && (
        <span
          data-slot="dot"
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-current"
        />
      )}
      <span data-type-role={typeRole}>{label}</span>
    </span>
  );
}
